import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CalendarHeader from '../components/calendar/CalendarHeader';
import CalendarGrid from '../components/calendar/CalendarGrid';
import TimeSlots from '../components/calendar/TimeSlots';

const { FiCalendar, FiCheck, FiArrowRight, FiUser, FiPhone, FiSettings, FiHash } = FiIcons;

const generateBookingID = () => {
  const num = Math.floor(1000 + Math.random() * 9000);
  return `TES-2026-${num}`;
};

const readJsonSafe = async (response) => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { rawText: text };
  }
};

const Appointments = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: '', phone: '', service: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [bookingID, setBookingID] = useState('');

  const services = [
    'Electrical Installation', 'CCTV Setup', 'Smart Home Automation',
    'Solar Maintenance', 'Emergency Repair', 'Security Audit'
  ];

  const handleBooking = async (e) => {
    e.preventDefault();
    setError('');

    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!formData.name.trim() || !formData.service.trim()) {
      setError('Please enter your name and select a service.');
      return;
    }
    if (cleanPhone.length < 10) {
      setError('Please enter a valid WhatsApp number with at least 10 digits.');
      return;
    }

    setLoading(true);

    const id = generateBookingID();
    const bookingDetails = {
      name: formData.name,
      phone: cleanPhone,
      service: formData.service,
      date: format(selectedDate, 'PPPP'),
      time: selectedTime,
      bookingId: id,
      source: 'Appointment Calendar',
    };

    try {
      // ✅ Now calls YOUR Netlify API — keys are hidden!
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingDetails),
      });

      const data = await readJsonSafe(response);

      if (!response.ok) {
        const looksLikeHtml = typeof data?.rawText === 'string' && data.rawText.toLowerCase().includes('<!doctype');
        if (response.status === 404 || looksLikeHtml) {
          throw new Error('Local API route not available. Run the site with Netlify dev so /api/booking works.');
        }

        throw new Error(data?.error || `Booking failed (${response.status})`);
      }

      setBookingID(id);
      setSuccess(true);

      setTimeout(() => {
        const whatsappMsg =
          `✅ Appointment Confirmed!\n\n` +
          `🆔 Booking ID: ${id}\n` +
          `👤 Name: ${formData.name}\n` +
          `📞 Phone: ${formData.phone}\n` +
          `🔧 Service: ${formData.service}\n` +
          `📅 Date: ${bookingDetails.date}\n` +
          `⏰ Time: ${selectedTime}`;
        window.open(`https://wa.me/919906669918?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      }, 1500);

    } catch (err) {
      console.error('Booking failed:', err);
      setError(err.message || 'Could not save booking. Please try again or contact us via WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSuccess(false);
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({ name: '', phone: '', service: '' });
    setBookingID('');
    setError('');
  };

  return (
    <div className="min-h-screen pt-16 md:pt-20 bg-black">
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-cyan-900/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-6"
            >
              <SafeIcon icon={FiCalendar} className="text-lg" />
              <span>Book Your Professional Service</span>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Service <span className="gradient-text">Appointment</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Schedule a professional visit at your convenience. Select a date and time to get started.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl overflow-hidden shadow-2xl">
              <div className="grid grid-cols-1 lg:grid-cols-5">

                {/* Sidebar */}
                <div className="lg:col-span-2 bg-gray-800/30 p-8 border-b lg:border-b-0 lg:border-r border-gray-800">
                  <div className="space-y-8">
                    {[
                      { num: 1, label: 'Choose Date', sub: 'Pick your preferred day' },
                      { num: 2, label: 'Select Time', sub: 'Available slots for the day' },
                      { num: 3, label: 'Your Details', sub: 'Contact information' }
                    ].map((s) => (
                      <div key={s.num} className="flex items-start space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                          success || step > s.num ? 'bg-green-600 text-white'
                          : step === s.num ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                          : 'bg-gray-800 text-gray-500'
                        }`}>
                          {success || step > s.num ? <SafeIcon icon={FiCheck} /> : s.num}
                        </div>
                        <div>
                          <p className={`font-semibold ${step >= s.num || success ? 'text-white' : 'text-gray-500'}`}>{s.label}</p>
                          <p className="text-xs text-gray-500">{s.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedDate && selectedTime && step === 3 && !success && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-10 bg-blue-600/10 border border-blue-500/20 rounded-xl p-4 space-y-2"
                    >
                      <p className="text-blue-300 text-xs font-semibold uppercase tracking-wider">Your Booking</p>
                      <p className="text-white text-sm font-medium">{format(selectedDate, 'EEEE, MMMM do')}</p>
                      <p className="text-cyan-400 text-sm font-bold">{selectedTime}</p>
                    </motion.div>
                  )}
                </div>

                {/* Main area */}
                <div className="lg:col-span-3">
                  <AnimatePresence mode="wait">
                    {success ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-10 text-center h-full flex flex-col items-center justify-center min-h-[420px]"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                          className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-5 border-2 border-green-500/40"
                        >
                          <SafeIcon icon={FiCheck} className="text-4xl text-green-400" />
                        </motion.div>
                        <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed! 🎉</h2>
                        <p className="text-gray-400 text-sm mb-6">Your appointment has been successfully scheduled.</p>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/40 rounded-2xl px-6 py-4 mb-6 w-full max-w-xs"
                        >
                          <p className="text-gray-400 text-xs uppercase tracking-widest mb-1 flex items-center justify-center space-x-1">
                            <SafeIcon icon={FiHash} className="text-blue-400" />
                            <span>Booking ID</span>
                          </p>
                          <p className="text-2xl font-black text-white tracking-widest">{bookingID}</p>
                          <p className="text-gray-500 text-xs mt-1">Save this for your records</p>
                        </motion.div>
                        <div className="text-center mb-6 space-y-1">
                          <p className="text-blue-400 font-semibold">{format(selectedDate, 'EEEE, MMMM do, yyyy')}</p>
                          <p className="text-cyan-400 font-bold text-xl">{selectedTime}</p>
                          <p className="text-gray-400 text-sm">{formData.service}</p>
                        </div>
                        <p className="text-gray-500 text-xs mb-6">WhatsApp is opening to send you a confirmation.</p>
                        <button onClick={handleReset} className="btn-primary px-8 py-3">
                          Book Another Appointment
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="h-full"
                      >
                        {step === 1 && (
                          <div className="flex flex-col h-full">
                            <CalendarHeader currentMonth={currentMonth} onMonthChange={setCurrentMonth} />
                            <CalendarGrid
                              currentMonth={currentMonth}
                              selectedDate={selectedDate}
                              onDateSelect={(d) => { setSelectedDate(d); setStep(2); }}
                            />
                          </div>
                        )}
                        {step === 2 && (
                          <div className="flex flex-col h-full">
                            <div className="p-6 border-b border-gray-700/50 flex items-center justify-between">
                              <h3 className="text-lg font-bold text-white">{format(selectedDate, 'EEEE, MMMM do')}</h3>
                              <button onClick={() => setStep(1)} className="text-blue-400 text-sm hover:underline">Change Date</button>
                            </div>
                            <TimeSlots
                              selectedTime={selectedTime}
                              onTimeSelect={(t) => { setSelectedTime(t); setStep(3); }}
                            />
                          </div>
                        )}
                        {step === 3 && (
                          <form onSubmit={handleBooking} className="p-8 space-y-5">
                            <div className="flex items-center justify-between mb-2">
                              <div className="bg-blue-600/20 text-blue-400 px-3 py-1 rounded-full text-xs font-bold">
                                {format(selectedDate, 'MMM d')} @ {selectedTime}
                              </div>
                              <button type="button" onClick={() => setStep(2)} className="text-blue-400 text-sm hover:underline">Change Time</button>
                            </div>
                            {error && (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm"
                              >
                                {error}
                              </motion.div>
                            )}
                            <div className="space-y-4">
                              <div className="relative">
                                <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                                <input
                                  type="text" required placeholder="Your Name"
                                  value={formData.name}
                                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors"
                                />
                              </div>
                              <div className="relative">
                                <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                                <input
                                  type="tel" required placeholder="WhatsApp Number"
                                  value={formData.phone}
                                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                  minLength={10}
                                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors"
                                />
                              </div>
                              <div className="relative">
                                <SafeIcon icon={FiSettings} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                                <select
                                  required value={formData.service}
                                  onChange={e => setFormData({ ...formData, service: e.target.value })}
                                  className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors appearance-none"
                                >
                                  <option value="">Select Service</option>
                                  {services.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>
                            </div>
                            <button
                              type="submit"
                              disabled={loading}
                              className="w-full btn-primary py-4 flex items-center justify-center space-x-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span>{loading ? 'Saving Appointment...' : 'Confirm Appointment'}</span>
                              {!loading && <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />}
                            </button>
                          </form>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Appointments;
